import React from "react";
import { LoanStatusDetails } from "../../interface/MembersPortal/ILoan";
import helpers from "../../helpers/index";

interface ApplicationForLoanFormProps {
    loanId?: string;
    loanStatusDetails: LoanStatusDetails | null;
}

function ApplicationForLoanForm({ loanId, loanStatusDetails }: ApplicationForLoanFormProps) {

    const fullName = `${loanStatusDetails?.firstname ?? ''} ${loanStatusDetails?.lastname ?? ''}`.trim();

    const comaker1MiddleInitial = loanStatusDetails?.comakerMiddlename1 ? `${loanStatusDetails.comakerMiddlename1.charAt(0).toUpperCase()}.` : '';
    const comaker1FullName = [loanStatusDetails?.comakerFirstname1, comaker1MiddleInitial, loanStatusDetails?.comakerLastname1].filter(Boolean).join(' ');

    const comaker2MiddleInitial = loanStatusDetails?.comakerMiddlename2 ? `${loanStatusDetails.comakerMiddlename2.charAt(0).toUpperCase()}.` : '';
    const comaker2FullName = [loanStatusDetails?.comakerFirstname2, comaker2MiddleInitial, loanStatusDetails?.comakerLastname2].filter(Boolean).join(' ');

    const amountLoan = Number(loanStatusDetails?.amountLoan ?? 0);
    const term = loanStatusDetails?.term ?? 0;
    const amortization = Number(loanStatusDetails?.amortizationSchedule ?? 0);

    const fieldLine = (value: React.ReactNode, minWidth = '150px') => (
        <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth, textAlign: 'center', padding: '0 4px' }}>
            {value}
        </span>
    );

    const emptyLine = (minWidth = '150px') => (
        <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth }}>&nbsp;</span>
    );

    return (
        <div style={{ maxWidth: '820px', margin: '0 auto', fontFamily: 'Times New Roman, serif', fontSize: '13px', padding: '32px', background: '#fff', border: '1px solid #ccc' }}>

            {/* KLF No. label */}
            <div style={{ fontSize: '11px', marginBottom: '6px' }}>
                KLF No. 02-004 (LOANS P10,500 to P70,000.00)
            </div>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                <div style={{ fontWeight: 'bold' }}>KOOP ng mga KAWANI NG PAMAHALAANG</div>
                <div style={{ fontWeight: 'bold' }}>PANLALAWIGAN NG BATANGAN MPC</div>
                <div style={{ fontWeight: 'bold' }}>(KOOPBATANGAN MPC)</div>
                <br />
                <div style={{ fontWeight: 'bold', fontSize: '15px', textDecoration: 'underline', letterSpacing: '1px' }}>
                    APPLICATION FOR LOAN
                </div>
            </div>

            {/* Account Numbers & Department/Date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div>
                    <span>Loan Account No. </span>
                    {fieldLine(loanId, '120px')}
                </div>
                <div style={{ textAlign: 'center' }}>
                    {fieldLine(loanStatusDetails?.departmentName ?? ' ', '200px')}
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>Department/Office</div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
                <div>
                    <span>Savings Account No. </span>
                    {fieldLine(loanStatusDetails?.membershipId, '120px')}
                </div>
                <div style={{ textAlign: 'center' }}>
                    {emptyLine('200px')}
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>Date</div>
                </div>
            </div>

            {/* Application Body */}
            <div style={{ marginBottom: '16px', lineHeight: '2.2' }}>
                <span>I, </span>
                {fieldLine(<strong>{fullName}</strong>, '220px')}
                <span>, hereby apply for a loan to KOOPBATANGAN MULTI-PURPOSE COOPERATIVE amounting to </span>
                {fieldLine(amountLoan.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '160px')}
                <span> (P </span>
                {fieldLine(<strong>{amountLoan.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>, '120px')}
                <span>) to be paid in </span>
                {fieldLine(term, '60px')}
                <span> (</span>
                {fieldLine(term, '60px')}
                <span>) months in a semi - monthly installment of </span>
                {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                <span> (</span>
                {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                <span>)</span>
            </div>

            {/* Loan Purpose */}
            <div style={{ marginBottom: '10px' }}>
                <span>I, desire this loan for &nbsp;&nbsp;</span>
                <span style={{ marginRight: '24px' }}>
                    <input type="checkbox" disabled style={{ marginRight: '4px' }} />productive
                </span>
                <span>
                    <input type="checkbox" disabled style={{ marginRight: '4px' }} />provident purpose(s)
                </span>
            </div>

            <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <span>1.</span>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'inline-block', textAlign: 'center', minWidth: '220px' }}>
                        {loanStatusDetails?.comakerSignature1 && (
                            <img src={loanStatusDetails.comakerSignature1} alt="Co-maker 1 Signature" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        <span style={{ display: 'block', borderBottom: '1px solid #000', fontSize: '12px', padding: '0 2px' }}>
                            {comaker1FullName || ' '}
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '4px' }}>
                        <span>2.</span>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-block', textAlign: 'center', minWidth: '220px' }}>
                                {loanStatusDetails?.comakerSignature2 && (
                                    <img src={loanStatusDetails.comakerSignature2} alt="Co-maker 2 Signature" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                                )}
                                <span style={{ display: 'block', borderBottom: '1px solid #000', fontSize: '12px', padding: '0 2px' }}>
                                    {comaker2FullName || ' '}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ fontSize: '11px', textAlign: 'center', marginLeft: '16px' }}>Name/Signature of Co-makers</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    {loanStatusDetails?.memberSignature
                        ? <img id="memberSignature" src={loanStatusDetails.memberSignature} alt="Applicant's Signature" style={{ width: '200px', height: '60px', objectFit: 'contain', borderBottom: '1px solid #000', display: 'block' }} />
                        : <span id="memberSignature" style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px' }}>&nbsp;</span>
                    }
                    <div style={{ fontSize: '11px' }}>Applicant's Signature</div>
                </div>
            </div>

            {/* Recommending / Approved */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div><strong>RECOMMENDING APPROVAL:</strong></div>
                <div><strong>APPROVED:</strong></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div></div>
                <div id="dvCreditCommittee1">{emptyLine('220px')}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div></div>
                <div id="dvCreditCommittee2">{emptyLine('220px')}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div id="dvGeneralManager">{emptyLine('220px')}</div>
                <div id="dvCreditCommittee3">{emptyLine('220px')}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', textAlign: 'center', width: '220px' }}>General Manager</div>
                <div style={{ fontSize: '11px', textAlign: 'center', width: '220px' }}>Credit Comittee</div>
            </div>

            <div style={{ fontSize: '11px', fontStyle: 'italic', marginBottom: '10px' }}>
                Note: Loan officer should sign initial on recommendation.
            </div>

            <hr style={{ border: 'none', borderTop: '2px dashed #000', marginBottom: '20px' }} />

            {/* Contract of Payment */}
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '15px', textDecoration: 'underline', letterSpacing: '1px', marginBottom: '16px' }}>
                CONTRACT OF PAYMENT
            </div>

            <div style={{ marginBottom: '16px', lineHeight: '2.2' }}>
                <span>I, </span>
                {fieldLine(<strong>{fullName}</strong>, '260px')}
                <span>, hereby agree to pay my loan to KOOPBATANGAN MPC based on the following terms and conditions:</span>
            </div>

            <ol style={{ marginBottom: '24px', lineHeight: '1.8', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '10px' }}>
                    <span>That I am allowing to deduct thru payroll my monthly amortization in the amount of </span>
                    {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '180px')}
                    <span> (P </span>
                    {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                    <span>)</span>
                    <br />
                    <em>This serves also as waiver in effect.</em>
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That I will pay a corresponding penalty of 2% in violation of the provision of existing policy and will affect my credit standing to the cooperative.
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That I will abide with the provisions specified in the General Guidelines in KOOPBATANGAN Lending and KOOPBATANGAN Credit/Lending Policies and other existing provisions in R.A. 6938, and related laws, Articles of Cooperation and By-laws, rules and regulation, statutory laws and ordinances of Provincial Government of Batangas as well as LGU/Prov'l Treasurer KOOPBATANGAN Memorandum of Understanding (MOU) pertaining thereto.
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That if at any time I will retire, I am authorizing KOOPBATANGAN to collect the balance of my loan from the monies that are due me from the <u>Provincial Government of Batangas.</u>
                </li>
            </ol>

            {/* Signatures */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', marginTop: '32px' }}>
                <div style={{ textAlign: 'center' }}>
                    {loanStatusDetails?.memberSignature
                        ? <img id="memberSignature" src={loanStatusDetails.memberSignature} alt="Applicant's Signature" style={{ width: '200px', height: '60px', objectFit: 'contain', borderBottom: '1px solid #000', display: 'block' }} />
                        : <span id="memberSignature" style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px' }}>&nbsp;</span>
                    }
                    <div style={{ fontSize: '11px' }}>Applicant's Signature</div>
                </div>
                <div>
                    <div>Notary Public</div>
                </div>
            </div>

            {/* Notary fields */}
            <div style={{ fontSize: '12px', lineHeight: '2' }}>
                <div>Doc. No. &nbsp;{emptyLine('130px')}</div>
                <div>Page. No. {emptyLine('130px')}</div>
                <div>Book. No. {emptyLine('130px')}</div>
                <div>Series of {emptyLine('130px')}</div>
            </div>

        </div>
    );
}

export default ApplicationForLoanForm;
